import { NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { DOCTOR } from "@/lib/data";

const TABS = [
  { to: "/", label: "About" },
  { to: "/conditions", label: "Conditions" },
  { to: "/book", label: "Book Appointment" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const onDark = pathname.startsWith("/reviews");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled && !onDark
          ? "bg-beige-100/85 backdrop-blur-xl border-b border-navy-600/10"
          : onDark
          ? "bg-transparent"
          : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="mx-auto max-w-[1600px] px-6 lg:px-12 flex items-center justify-between py-5">
        <Link to="/" className="flex items-baseline gap-3" data-testid="brand-link">
          <span className={`font-serif text-xl md:text-2xl tracking-tight ${onDark ? "text-beige-100" : "text-navy-600"}`}>Dr. Vikas Matai</span>
          <span className={`hidden md:inline text-[10px] uppercase tracking-[0.28em] ${onDark ? "text-beige-100/70" : "text-navy-500/70"}`}>
            Surgeon
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {TABS.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              data-testid={`nav-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `link-underline text-[11px] uppercase tracking-[0.24em] transition-colors ${
                  onDark
                    ? isActive
                      ? "text-beige-100"
                      : "text-beige-100/70 hover:text-beige-100"
                    : isActive
                    ? "text-navy-600"
                    : "text-navy-600/70 hover:text-navy-600"
                }`
              }
              end={t.to === "/"}
            >
              {t.label}
            </NavLink>
          ))}
          <a
            href={DOCTOR.googleReviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-reviews"
            className={`link-underline text-[11px] uppercase tracking-[0.24em] ${
              onDark ? "text-beige-100/70 hover:text-beige-100" : "text-navy-600/70 hover:text-navy-600"
            }`}
          >
            Reviews ↗
          </a>
        </nav>

        <Link
          to="/book"
          data-testid="nav-cta-book"
          className={`hidden lg:inline-flex items-center gap-2 px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] transition-colors ${
            onDark
              ? "bg-beige-100 text-navy-600 hover:bg-brass-400"
              : "bg-navy-600 text-beige-100 hover:bg-navy-700"
          }`}
        >
          Reserve a slot
        </Link>

        <button
          onClick={() => setOpen(true)}
          data-testid="mobile-menu-open"
          className={`lg:hidden ${onDark ? "text-beige-100" : "text-navy-600"}`}
          aria-label="Open menu"
        >
          <Menu size={22} />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.55, ease: [0.65, 0, 0.35, 1] }}
            className="fixed inset-0 bg-navy-600 z-[60] flex flex-col p-8"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between">
              <span className="font-serif text-xl text-beige-100">Dr. Vikas Matai</span>
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" className="text-beige-100" aria-label="Close menu">
                <X size={22} />
              </button>
            </div>
            <div className="mt-16 flex flex-col gap-6">
              {TABS.map((t, i) => (
                <NavLink
                  key={t.to}
                  to={t.to}
                  onClick={() => setOpen(false)}
                  end={t.to === "/"}
                  className="font-serif text-4xl text-beige-100"
                  data-testid={`mobile-nav-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span className="text-brass-400 text-xs align-top mr-3">0{i + 1}</span>
                  {t.label}
                </NavLink>
              ))}
              <a
                href={DOCTOR.googleReviewsUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-reviews"
                className="font-serif text-4xl text-beige-100"
              >
                <span className="text-brass-400 text-xs align-top mr-3">04</span>
                Reviews ↗
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
